
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent, 
  ChartLegend, 
  ChartLegendContent 
} from "@/components/ui/chart";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { SystemMetric } from "@/pages/tech/SystemMonitor";

interface UserActivityChartProps {
  data: SystemMetric[];
  isLoading: boolean;
  activeUsers: number;
  avgSessionTime: number;
}

const UserActivityChart: React.FC<UserActivityChartProps> = ({ 
  data, 
  isLoading,
  activeUsers,
  avgSessionTime
}) => {
  console.log("UserActivityChart - Received data:", data);
  console.log("UserActivityChart - Active users:", activeUsers);
  console.log("UserActivityChart - Avg session time:", avgSessionTime);

  // Set up chart colors for our metrics
  const chartConfig = {
    activeUsers: { label: "Active Users", color: "#004161" },
    newUsers: { label: "New Users", color: "#0f8f8c" }
  };

  // Extract the last 7 data points (last week)
  const lastWeekData = React.useMemo(() => {
    if (!data || data.length === 0) {
      console.log("UserActivityChart - No data to process");
      return [];
    }
    
    const result = [...data].slice(-7);
    console.log("UserActivityChart - Last week data:", result);
    return result;
  }, [data]);

  // Display loading skeletons when data is being fetched
  if (isLoading) {
    console.log("UserActivityChart - Showing loading state");
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Card className="bg-white h-full">
            <CardHeader className="pb-0">
              <CardTitle className="text-xl font-title text-primary">User Activity (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <Skeleton className="h-[350px] w-full" />
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col gap-4">
          <Card className="bg-white">
            <CardContent className="p-6">
              <Skeleton className="h-16 w-full mb-2" />
              <Skeleton className="h-6 w-full" />
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-6">
              <Skeleton className="h-16 w-full mb-2" />
              <Skeleton className="h-6 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Display a message if no data is available
  if (!data || data.length === 0) {
    console.log("UserActivityChart - No data available");
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white md:col-span-2">
          <CardHeader className="pb-0">
            <CardTitle className="text-xl font-title text-primary">User Activity (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 flex items-center justify-center h-[350px]">
            <p className="text-muted-foreground text-center">No user activity data available. Please check database connectivity or ensure metrics are being collected.</p>
          </CardContent>
        </Card>
        <div className="flex flex-col gap-4">
          <Card className="bg-white">
            <CardContent className="p-6 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold text-muted-foreground mb-2">--</span>
              <CardTitle className="text-xl font-title text-center">Current Active Users</CardTitle>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-6 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold text-muted-foreground mb-2">--</span>
              <CardTitle className="text-xl font-title text-center">Avg. Session Time (mins)</CardTitle>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If we have data, render the actual data with no synthetic values
  console.log("UserActivityChart - Rendering with data:", lastWeekData);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="bg-white md:col-span-2">
        <CardHeader className="pb-0">
          <CardTitle className="text-xl font-title text-primary">
            User Activity (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="h-[350px]">
            <ChartContainer 
              config={chartConfig}
              className="h-full w-full"
            >
              <BarChart 
                data={lastWeekData} 
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1eceb" />
                <XAxis 
                  dataKey="metric_date" 
                  stroke="#6c6c6c" 
                  angle={-45} 
                  textAnchor="end" 
                  height={60} 
                  padding={{ left: 10, right: 10 }}
                />
                <YAxis 
                  stroke="#6c6c6c" 
                  width={60}
                  orientation="left"
                  yAxisId={0}
                  domain={['auto', 'auto']}
                  padding={{ top: 20, bottom: 20 }}
                  allowDecimals={true}
                  allowDataOverflow={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar 
                  dataKey="active_users" 
                  name="activeUsers" 
                  fill="#004161" 
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={true}
                  animationDuration={1000}
                />
                <Bar 
                  dataKey="new_users" 
                  name="newUsers" 
                  fill="#0f8f8c" 
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={true}
                  animationDuration={1000}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-4">
        <Card className="bg-white">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold text-primary mb-2">
              {activeUsers !== 0 ? activeUsers : '--'}
            </span>
            <CardTitle className="text-xl font-title text-center">
              Current Active Users
            </CardTitle>
          </CardContent>
        </Card>
        
        <Card className="bg-white">
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold text-accent mb-2">
              {avgSessionTime !== 0 ? avgSessionTime : '--'}
            </span>
            <CardTitle className="text-xl font-title text-center">
              Avg. Session Time (mins)
            </CardTitle>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserActivityChart;
